import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function ArchivedItems() {
  const { projectKey } = useParams();
  const [projectId, setProjectId] = useState(null);
  const [project, setProject] = useState(null);
  const [archivedData, setArchivedData] = useState({
    folders: [],
    testCases: [],
    requirements: [],
    testSuites: [],
  });
  const [loading, setLoading] = useState(true);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameItem, setRenameItem] = useState(null);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (projectKey) {
      fetchProject();
    }
  }, [projectKey]);

  useEffect(() => {
    if (projectId) {
      fetchArchivedItems();
    }
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/projects/${projectKey}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const projectData = data.data || data;
        if (projectData) {
          setProject(projectData);
          setProjectId(projectData.id || projectData._id);
        }
      }
    } catch (err) {
      console.error('Error loading project:', err);
    }
  };

  const fetchArchivedItems = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/archive/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setArchivedData(data.data || {
          folders: [],
          testCases: [],
          requirements: [],
          testSuites: [],
        });
      }
    } catch (err) {
      console.error('Error loading archived items:', err);
      setError('Failed to load archived items');
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchive = async (item, type) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = `${API_URL}/archive/restore/${type}/${item.id || item._id}`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 409) {
        const data = await response.json();
        setRenameItem({ ...item, type, endpoint });
        setNewName(item.name || item.title || '');
        setShowRenameModal(true);
      } else if (response.ok) {
        setError('');
        alert(`${type.slice(0, -1)} restored successfully!`);
        fetchArchivedItems();
      } else {
        const data = await response.json();
        setError(`Failed to restore: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error unarchiving item:', err);
      setError('Failed to restore item');
    }
  };

  const handleRenameAndRestore = async () => {
    if (!newName.trim()) {
      alert('Please enter a new name');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const body = renameItem.type === 'folders' || renameItem.type === 'suites'
        ? { newName: newName.trim() }
        : { newTitle: newName.trim() };

      const response = await fetch(renameItem.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        alert(`${renameItem.type.slice(0, -1)} restored successfully with new name!`);
        setShowRenameModal(false);
        setRenameItem(null);
        setNewName('');
        setError('');
        fetchArchivedItems();
      } else {
        const data = await response.json();
        setError(`Failed to restore: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error renaming and restoring item:', err);
      setError('Failed to restore item');
    }
  };

  const renderFolderTree = (folders, parentId = null) => {
    const children = folders.filter(f => {
      const fParentId = f.parentId?.id || f.parentId?._id || f.parentId;
      return parentId === null ? !fParentId : fParentId === parentId;
    });

    return children.map(folder => {
      const folderId = folder.id || folder._id;
      return (
        <div key={folderId} style={{ marginLeft: parentId ? '20px' : '0' }} className="mb-2">
          <div className="card">
            <div className="card-body py-2">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <span className="me-2">📁</span>
                  <strong>{folder.name}</strong>
                  <small className="text-muted ms-2">({folder.type})</small>
                  {folder.description && <small className="text-muted ms-2">{folder.description}</small>}
                </div>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => handleUnarchive(folder, 'folders')}
                  title="Restore folder and contents"
                >
                  ↩ Restore
                </button>
              </div>
            </div>
          </div>
          {renderFolderTree(folders, folderId)}
        </div>
      );
    });
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  const totalArchived = 
    archivedData.folders.length +
    archivedData.testCases.length +
    archivedData.requirements.length +
    archivedData.testSuites.length;

  return (
    <div className="container mt-4">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/projects">Projects</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to={`/projects/${projectId}`}>{project?.name}</Link>
          </li>
          <li className="breadcrumb-item active">Archived Items</li>
        </ol>
      </nav>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>🗄️ Archived Items</h2>
        <span className="badge bg-secondary">{totalArchived} total items</span>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}

      {totalArchived === 0 ? (
        <div className="alert alert-info">
          <strong>No archived items.</strong> Archive folders, test cases, requirements, or test suites to see them here.
        </div>
      ) : (
        <>
          {/* Archived Folders */}
          {archivedData.folders.length > 0 && (
            <div className="mb-5">
              <h4 className="mb-3">
                📁 Folders ({archivedData.folders.length})
              </h4>
              {renderFolderTree(archivedData.folders)}
            </div>
          )}

          {/* Archived Requirements */}
          {archivedData.requirements.length > 0 && (
            <div className="mb-5">
              <h4 className="mb-3">
                📄 Requirements ({archivedData.requirements.length})
              </h4>
              {archivedData.requirements.map(req => (
                <div key={req.id || req._id} className="card mb-2">
                  <div className="card-body py-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{req.title}</strong>
                        {req.type && <span className="badge bg-secondary ms-2">{req.type}</span>}
                        {req.status && <span className="badge bg-info ms-2">{req.status}</span>}
                      </div>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleUnarchive(req, 'requirements')}
                        title="Restore requirement"
                      >
                        ↩ Restore
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Archived Test Cases */}
          {archivedData.testCases.length > 0 && (
            <div className="mb-5">
              <h4 className="mb-3">
                ✓ Test Cases ({archivedData.testCases.length})
              </h4>
              {archivedData.testCases.map(tc => (
                <div key={tc.id || tc._id} className="card mb-2">
                  <div className="card-body py-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{tc.title}</strong>
                        {tc.priority && <span className="badge bg-warning ms-2">{tc.priority}</span>}
                        {tc.status && <span className="badge bg-success ms-2">{tc.status}</span>}
                      </div>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleUnarchive(tc, 'testcases')}
                        title="Restore test case"
                      >
                        ↩ Restore
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Archived Test Suites */}
          {archivedData.testSuites.length > 0 && (
            <div className="mb-5">
              <h4 className="mb-3">
                📦 Test Suites ({archivedData.testSuites.length})
              </h4>
              {archivedData.testSuites.map(suite => (
                <div key={suite.id || suite._id} className="card mb-2">
                  <div className="card-body py-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{suite.name}</strong>
                        {suite.description && <small className="text-muted ms-2">{suite.description}</small>}
                      </div>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleUnarchive(suite, 'suites')}
                        title="Restore test suite"
                      >
                        ↩ Restore
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Rename Modal */}
      {showRenameModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Duplicate Name Detected</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowRenameModal(false);
                    setRenameItem(null);
                    setNewName('');
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  A {renameItem?.type.slice(0, -1)} with the name "<strong>{renameItem?.name || renameItem?.title}</strong>" already exists.
                </p>
                <p>Please enter a new name to restore this item:</p>
                <input
                  type="text"
                  className="form-control"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Enter new name"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleRenameAndRestore();
                    }
                  }}
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowRenameModal(false);
                    setRenameItem(null);
                    setNewName('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleRenameAndRestore}
                  disabled={!newName.trim()}
                >
                  Restore with New Name
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ArchivedItems;
